export const WCAG_VERSION_OPTIONS = [
  'WCAG 2.1 A',
  'WCAG 2.1 AA',
  'WCAG 2.1 AAA',
  'WCAG 2.2 A',
  'WCAG 2.2 AA',
  'WCAG 2.2 AAA',
] as const;

export type WcagVersionOption = (typeof WCAG_VERSION_OPTIONS)[number];
export type WcagConformanceLevel = 'A' | 'AA' | 'AAA';
export type WcagRelease = '2.1' | '2.2';

export interface WcagCriterionOption {
  id: string;
  name: string;
  label: string;
  level: WcagConformanceLevel;
  introducedIn: WcagRelease;
}

const WCAG_LEVEL_RANK: Record<WcagConformanceLevel, number> = {
  A: 1,
  AA: 2,
  AAA: 3,
};

function criterion(
  id: string,
  name: string,
  level: WcagConformanceLevel,
  introducedIn: WcagRelease = '2.1',
): WcagCriterionOption {
  return {
    id,
    name,
    label: `${id} ${name}`,
    level,
    introducedIn,
  };
}

export const WCAG_CRITERIA: WcagCriterionOption[] = [
  criterion('1.1.1', 'Non-text Content', 'A'),
  criterion('1.2.1', 'Audio-only and Video-only (Prerecorded)', 'A'),
  criterion('1.2.2', 'Captions (Prerecorded)', 'A'),
  criterion('1.2.3', 'Audio Description or Media Alternative (Prerecorded)', 'A'),
  criterion('1.2.4', 'Captions (Live)', 'AA'),
  criterion('1.2.5', 'Audio Description (Prerecorded)', 'AA'),
  criterion('1.2.6', 'Sign Language (Prerecorded)', 'AAA'),
  criterion('1.2.7', 'Extended Audio Description (Prerecorded)', 'AAA'),
  criterion('1.2.8', 'Media Alternative (Prerecorded)', 'AAA'),
  criterion('1.2.9', 'Audio-only (Live)', 'AAA'),
  criterion('1.3.1', 'Info and Relationships', 'A'),
  criterion('1.3.2', 'Meaningful Sequence', 'A'),
  criterion('1.3.3', 'Sensory Characteristics', 'A'),
  criterion('1.3.4', 'Orientation', 'AA'),
  criterion('1.3.5', 'Identify Input Purpose', 'AA'),
  criterion('1.3.6', 'Identify Purpose', 'AAA'),
  criterion('1.4.1', 'Use of Color', 'A'),
  criterion('1.4.2', 'Audio Control', 'A'),
  criterion('1.4.3', 'Contrast (Minimum)', 'AA'),
  criterion('1.4.4', 'Resize Text', 'AA'),
  criterion('1.4.5', 'Images of Text', 'AA'),
  criterion('1.4.6', 'Contrast (Enhanced)', 'AAA'),
  criterion('1.4.7', 'Low or No Background Audio', 'AAA'),
  criterion('1.4.8', 'Visual Presentation', 'AAA'),
  criterion('1.4.9', 'Images of Text (No Exception)', 'AAA'),
  criterion('1.4.10', 'Reflow', 'AA'),
  criterion('1.4.11', 'Non-text Contrast', 'AA'),
  criterion('1.4.12', 'Text Spacing', 'AA'),
  criterion('1.4.13', 'Content on Hover or Focus', 'AA'),
  criterion('2.1.1', 'Keyboard', 'A'),
  criterion('2.1.2', 'No Keyboard Trap', 'A'),
  criterion('2.1.3', 'Keyboard (No Exception)', 'AAA'),
  criterion('2.1.4', 'Character Key Shortcuts', 'A'),
  criterion('2.2.1', 'Timing Adjustable', 'A'),
  criterion('2.2.2', 'Pause, Stop, Hide', 'A'),
  criterion('2.2.3', 'No Timing', 'AAA'),
  criterion('2.2.4', 'Interruptions', 'AAA'),
  criterion('2.2.5', 'Re-authenticating', 'AAA'),
  criterion('2.2.6', 'Timeouts', 'AAA'),
  criterion('2.3.1', 'Three Flashes or Below Threshold', 'A'),
  criterion('2.3.2', 'Three Flashes', 'AAA'),
  criterion('2.3.3', 'Animation from Interactions', 'AAA'),
  criterion('2.4.1', 'Bypass Blocks', 'A'),
  criterion('2.4.2', 'Page Titled', 'A'),
  criterion('2.4.3', 'Focus Order', 'A'),
  criterion('2.4.4', 'Link Purpose (In Context)', 'A'),
  criterion('2.4.5', 'Multiple Ways', 'AA'),
  criterion('2.4.6', 'Headings and Labels', 'AA'),
  criterion('2.4.7', 'Focus Visible', 'AA'),
  criterion('2.4.8', 'Location', 'AAA'),
  criterion('2.4.9', 'Link Purpose (Link Only)', 'AAA'),
  criterion('2.4.10', 'Section Headings', 'AAA'),
  criterion('2.4.11', 'Focus Not Obscured (Minimum)', 'AA', '2.2'),
  criterion('2.4.12', 'Focus Not Obscured (Enhanced)', 'AAA', '2.2'),
  criterion('2.4.13', 'Focus Appearance', 'AAA', '2.2'),
  criterion('2.5.1', 'Pointer Gestures', 'A'),
  criterion('2.5.2', 'Pointer Cancellation', 'A'),
  criterion('2.5.3', 'Label in Name', 'A'),
  criterion('2.5.4', 'Motion Actuation', 'A'),
  criterion('2.5.5', 'Target Size (Enhanced)', 'AAA'),
  criterion('2.5.6', 'Concurrent Input Mechanisms', 'AAA'),
  criterion('2.5.7', 'Dragging Movements', 'AA', '2.2'),
  criterion('2.5.8', 'Target Size (Minimum)', 'AA', '2.2'),
  criterion('3.1.1', 'Language of Page', 'A'),
  criterion('3.1.2', 'Language of Parts', 'AA'),
  criterion('3.1.3', 'Unusual Words', 'AAA'),
  criterion('3.1.4', 'Abbreviations', 'AAA'),
  criterion('3.1.5', 'Reading Level', 'AAA'),
  criterion('3.1.6', 'Pronunciation', 'AAA'),
  criterion('3.2.1', 'On Focus', 'A'),
  criterion('3.2.2', 'On Input', 'A'),
  criterion('3.2.3', 'Consistent Navigation', 'AA'),
  criterion('3.2.4', 'Consistent Identification', 'AA'),
  criterion('3.2.5', 'Change on Request', 'AAA'),
  criterion('3.2.6', 'Consistent Help', 'A', '2.2'),
  criterion('3.3.1', 'Error Identification', 'A'),
  criterion('3.3.2', 'Labels or Instructions', 'A'),
  criterion('3.3.3', 'Error Suggestion', 'AA'),
  criterion('3.3.4', 'Error Prevention (Legal, Financial, Data)', 'AA'),
  criterion('3.3.5', 'Help', 'AAA'),
  criterion('3.3.6', 'Error Prevention (All)', 'AAA'),
  criterion('3.3.7', 'Redundant Entry', 'A', '2.2'),
  criterion('3.3.8', 'Accessible Authentication (Minimum)', 'AA', '2.2'),
  criterion('3.3.9', 'Accessible Authentication (Enhanced)', 'AAA', '2.2'),
  criterion('4.1.2', 'Name, Role, Value', 'A'),
  criterion('4.1.3', 'Status Messages', 'AA'),
];

function normalizeCriterionSearch(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

export function parseWcagVersionOption(
  wcagVersion?: WcagVersionOption | '' | null,
): { release: WcagRelease; level: WcagConformanceLevel } | null {
  if (!wcagVersion) {
    return null;
  }

  const match = /^WCAG (2\.[12]) (A{1,3})$/u.exec(wcagVersion);
  if (!match) {
    return null;
  }

  return {
    release: match[1] as WcagRelease,
    level: match[2] as WcagConformanceLevel,
  };
}

export function getWcagCriterionByLabel(label?: string | null): WcagCriterionOption | null {
  const normalizedLabel = normalizeCriterionSearch((label ?? '').trim());
  if (!normalizedLabel) {
    return null;
  }

  return WCAG_CRITERIA.find((criterionOption) => (
    normalizeCriterionSearch(criterionOption.label) === normalizedLabel
  )) ?? null;
}

export function isWcagCriterionRelevantToVersion(
  criterionOption: WcagCriterionOption,
  wcagVersion?: WcagVersionOption | '' | null,
): boolean {
  const selectedVersion = parseWcagVersionOption(wcagVersion);
  if (!selectedVersion) {
    return true;
  }

  if (selectedVersion.release === '2.1' && criterionOption.introducedIn !== '2.1') {
    return false;
  }

  return WCAG_LEVEL_RANK[criterionOption.level] <= WCAG_LEVEL_RANK[selectedVersion.level];
}

export function filterWcagCriteria(
  query = '',
  wcagVersion: WcagVersionOption | '' | null = null,
  limit = 8,
): WcagCriterionOption[] {
  const normalizedQuery = normalizeCriterionSearch(query.trim());
  const relevantCriteria = WCAG_CRITERIA.filter((criterionOption) => (
    isWcagCriterionRelevantToVersion(criterionOption, wcagVersion)
  ));

  const matches = normalizedQuery.length === 0
    ? relevantCriteria
    : relevantCriteria.filter((criterionOption) => (
      normalizeCriterionSearch(criterionOption.id).includes(normalizedQuery) ||
      normalizeCriterionSearch(criterionOption.name).includes(normalizedQuery) ||
      normalizeCriterionSearch(criterionOption.label).includes(normalizedQuery) ||
      normalizeCriterionSearch(criterionOption.level).includes(normalizedQuery)
    ));

  return limit > 0 ? matches.slice(0, limit) : matches;
}
